import { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { styled } from "styled-components";
import { XCircle as CancelIcon } from "react-feather";

import CircleButton from "./CircleButton";
import { savedPhotoToDataUri } from "../savedPhoto";

const FixedContainer = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  top: 0;
  background: black;
  color: white;
  text-align: center;
  z-index: 100;
`;

const WebcamContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const ResultingImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const ActionButtonsContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 10px;
  height: 90px;
  background: rgba(0, 0, 0, 0.85);
`;

const CancelIconContainer = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 44px;
  height: 44px;
  padding: 7px;
  cursor: pointer;
  background: transparent;
  border: 0;
  color: white;
`;

/** The words the camera screen says, in the client's language (i18n.ts). English when nobody says, so a caller that passes
 *  only the two it always passed still reads right. */
export type CameraLabels = {
  retake: string;
  usePhoto: string;
  /** the shutter's name for a screen reader (it is a round button with no words on it) */
  shutter?: string;
  /** the X in the corner */
  close?: string;
  /** 2026-10-26 — what the screen says when the camera would not open, or has not opened after a while */
  cameraBlocked?: string;
  /** …and the way forward it offers: the phone's own camera, through the page's file box */
  useCameraApp?: string;
  /** the photo just taken, for a screen reader */
  preview?: string;
  /** the phone's own camera's photo could not be read */
  unreadable?: string;
};

const ENGLISH: Required<CameraLabels> = {
  retake: "Retake",
  usePhoto: "Use Photo",
  shutter: "Take the photo",
  close: "Close the camera",
  cameraBlocked: "The camera didn't open here. You can take the photo with your phone's own camera instead.",
  useCameraApp: "Open my phone's camera",
  preview: "The photo you just took",
  unreadable: "That photo could not be opened. Try again.",
};

/** HOW LONG THE CAMERA MAY SAY NOTHING (2026-10-26, docs/FORM-AUDIT-2026-10-26.md). A browser that will never give the page a
 *  camera does not always say so: some in-app browsers leave the request unanswered, and the screen stayed black for as long
 *  as the client looked at it. Past this the way forward is offered beside the camera; the camera, if it comes, still works. */
export const CAMERA_PATIENCE_MS = 8000;

/** TW-0501 — WHY THE CAMERA DID NOT OPEN, as the beacon carries it (`form_camera_failed`'s `reason`): the browser's own reason,
 *  its DOMException's name in one word of ours, never its message. `dark` is a camera still showing nothing after
 *  CAMERA_PATIENCE_MS, `unsupported` a browser that gives the page no camera at all (react-webcam's own sentence for it). */
export const CAMERA_FAILURES = [
  "not_allowed", "not_found", "not_readable", "overconstrained", "security", "aborted", "unsupported", "dark", "other",
] as const;
export type CameraFailure = (typeof CAMERA_FAILURES)[number];

const BY_NAME: Record<string, CameraFailure> = {
  NotAllowedError: "not_allowed",
  PermissionDeniedError: "not_allowed", // an older Chrome's name for the same refusal
  NotFoundError: "not_found",
  DevicesNotFoundError: "not_found",
  NotReadableError: "not_readable",
  TrackStartError: "not_readable",
  OverconstrainedError: "overconstrained",
  ConstraintNotSatisfiedError: "overconstrained",
  SecurityError: "security",
  AbortError: "aborted",
};

/** The word for what react-webcam handed `onUserMediaError`: a DOMException (by its name), or, for a browser with no camera API
 *  at all, its own sentence ("getUserMedia not supported") or the error its polyfill rejects with ("getUserMedia is not
 *  implemented in this browser", which is what jsdom meets). The words are read here to choose the word, and never leave.
 *  Anything else is `other`. Pure. */
export function cameraFailure(error: unknown): CameraFailure {
  const NO_CAMERA_API = /not (supported|implemented)/i;
  if (typeof error === "string") return NO_CAMERA_API.test(error) ? "unsupported" : "other";
  if (typeof error !== "object" || error === null) return "other";
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (typeof name === "string" && BY_NAME[name]) return BY_NAME[name];
  return typeof message === "string" && NO_CAMERA_API.test(message) ? "unsupported" : "other";
}

function PhotoUploader({
  onReceivedPhoto,
  onCanceled,
  labels = ENGLISH,
  onCameraFailed,
}: {
  onReceivedPhoto: ({
    dataUri,
    dimensions,
  }: {
    dataUri: string;
    dimensions: { width: number; height: number };
  }) => void;
  onCanceled: () => void;
  /** the screen's words, in the client's language (i18n.ts); English when nobody says */
  labels?: CameraLabels;
  /** TW-0501 — told ONCE per opening of this screen when the camera does not open: the browser refused it, or it is still dark
   *  after CAMERA_PATIENCE_MS. The form beacons it (`form_camera_failed`); nothing here waits on it or can be stopped by it. */
  onCameraFailed?: (reason: CameraFailure) => void;
}) {
  const words: Required<CameraLabels> = { ...ENGLISH, ...labels };
  const webcamRef = useRef<Webcam>(null);

  // 2026-10-26 — A PRESS BEFORE THE FIRST FRAME IS TRIED AGAIN, never dropped. The camera's stream can be live a moment before
  // its video has a frame to draw, and react-webcam's getScreenshot() answers null until then: the press took nothing and said
  // nothing (docs/LOCAL.md measured it). So the shutter waits for the stream (CircleButton `disabled`), and a press that still
  // meets no frame tries again for a second and a half.
  const capture = useCallback(() => {
    const attempt = (left: number) => {
      const imageSrc = webcamRef.current?.getScreenshot();
      if (imageSrc) {
        setLiveDataUri(imageSrc);
        setPhotoDimensions({
          width: 1280,
          height: 720,
        });
        return;
      }
      if (left > 0) setTimeout(() => attempt(left - 1), 150);
    };
    attempt(10);
  }, []);

  const [liveDataUri, setLiveDataUri] = useState<string | null>(null);

  const [photoDimensions, setPhotoDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // 2026-10-26 — WHETHER THE CAMERA CAME. Until today a camera the browser refused (a "Don't Allow" tapped once, an in-app
  // browser with no camera for the page, a camera another app holds) left a black screen, a shutter that did nothing and a
  // small X, with not one word in either language; at a shop whose owner turned saved photos off there was no other way to
  // the ID photo, and the submit then said "You must upload a photo of your ID." (the most common refusal there is: 579 in 444
  // visits, 59 of them never signed, 2026-09-23 to 10-25). Now a refusal, or a camera still dark after CAMERA_PATIENCE_MS, says
  // so and offers the phone's own camera through a file box with `capture`, which every phone's browser hands to its camera
  // app without asking the page for anything.
  const [streaming, setStreaming] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [slow, setSlow] = useState(false);
  const [unreadable, setUnreadable] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), CAMERA_PATIENCE_MS);
    return () => clearTimeout(timer);
  }, []);
  // TW-0501 — the failure is told once per opening, whichever way it came first: a refusal, or a camera still dark at the
  // patience mark. Wrapped: a beacon that throws is a beacon, never a camera screen that breaks.
  const toldFailure = useRef(false);
  const tellFailure = useCallback(
    (reason: CameraFailure) => {
      if (toldFailure.current) return;
      toldFailure.current = true;
      try {
        onCameraFailed?.(reason);
      } catch {
        /* a photo is worth more than a data point */
      }
    },
    [onCameraFailed]
  );
  useEffect(() => {
    if (slow && !streaming && !blocked) tellFailure("dark");
  }, [slow, streaming, blocked, tellFailure]);
  const cameraApp = useRef<HTMLInputElement>(null);
  const onCameraAppPhoto = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const file = input.files?.[0];
      input.value = "";
      if (!file) return;
      // the phone's camera hands over a full-size photo: drawn down to the size and the JPEG the waiver posts (savedPhoto.ts)
      const result = await savedPhotoToDataUri(file);
      if (!result.ok) {
        setUnreadable(true);
        return;
      }
      onReceivedPhoto({ dataUri: result.dataUri, dimensions: { width: 0, height: 0 } });
    },
    [onReceivedPhoto]
  );
  const offerCameraApp = blocked || (slow && !streaming);

  useEffect(() => {
    // Make sure the body can't scroll while the camera is open
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const onUsePhoto = useCallback(() => {
    if (!liveDataUri) {
      console.warn("No liveDataUri");
      return;
    }
    if (!photoDimensions) {
      console.warn("No photoDimensions");
      return;
    }
    onReceivedPhoto({
      dataUri: liveDataUri,
      dimensions: photoDimensions,
    });
  }, [liveDataUri, onReceivedPhoto, photoDimensions]);

  return (
    <FixedContainer>
      {!blocked && (
        <WebcamContainer>
          <Webcam
            ref={webcamRef}
            audio={false}
            height={720}
            screenshotFormat="image/jpeg"
            width={1280}
            videoConstraints={{
              width: 1280,
              height: 720,
              facingMode: "environment",
            }}
            onUserMedia={() => setStreaming(true)}
            onUserMediaError={(error) => {
              setBlocked(true);
              tellFailure(cameraFailure(error));
            }}
          />
        </WebcamContainer>
      )}
      {offerCameraApp && !liveDataUri && (
        <div
          className="absolute left-0 right-0 px-6"
          style={{ top: blocked ? "30%" : 70, zIndex: 3 }}
          role="alert"
          data-testid="camera-blocked"
        >
          <p className="text-lg mb-4">{words.cameraBlocked}</p>
          <button
            type="button"
            className="bg-white text-black font-semibold py-3 px-5 rounded"
            onClick={() => cameraApp.current?.click()}
          >
            {words.useCameraApp}
          </button>
          {unreadable && <p className="mt-3 text-base font-semibold">{words.unreadable}</p>}
          <input
            ref={cameraApp}
            className="hidden"
            type="file"
            accept="image/*"
            capture="environment"
            aria-label={words.useCameraApp}
            data-testid="camera-app-input"
            onChange={onCameraAppPhoto}
          />
        </div>
      )}
      {!blocked && (
        <CircleButton
          isClicked={false}
          label={words.shutter}
          disabled={!streaming}
          onClick={() => {
            capture();
          }}
        />
      )}
      {liveDataUri && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            background: "black",
          }}
        >
          <ResultingImageContainer>
            {liveDataUri && (
              <img
                style={{
                  objectFit: "contain",
                }}
                src={liveDataUri}
                alt={words.preview}
              />
            )}
          </ResultingImageContainer>
          <ActionButtonsContainer>
            {/* Light retake button */}
            <button
              type="button"
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              onClick={() => setLiveDataUri(null)}
            >
              {words.retake}
            </button>
            <button
              type="button"
              className="bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded"
              onClick={() => {
                onUsePhoto();
              }}
            >
              {words.usePhoto}
            </button>
          </ActionButtonsContainer>
        </div>
      )}
      <CancelIconContainer type="button" onClick={onCanceled} aria-label={words.close} style={{ zIndex: 101 }}>
        <CancelIcon aria-hidden="true" />
      </CancelIconContainer>
    </FixedContainer>
  );
}

export default PhotoUploader;
